import * as React from 'react';
import WoWSEntity from '@wg/wows-entities/wrappers/react/WoWSEntity';
import {
    Currencies, EntityTypes, ItemTypes, PresentationStyles
} from '@wg/wows-entities/const';
import classnames from 'classnames';
import type Api from '~/types';
import styles from '~/Components/Dialogs/RulesDialog/RulesDialog.scss';
import { LANGUAGE_LOWER_FIRST_LETTER } from '~/constants';
import preloaded from '~/preloaded';

const ConditionalVehicleLink = ({ onClick, children }: {
    onClick?: () => void,
    children: JSX.Element,
}): JSX.Element => {
    if (!onClick) {
        return children;
    }

    return (
        <span className={'we-vehicle__inline-link'} onClick={onClick}>
            {children}
        </span>
    );
};

export const ship = (
    id: string,
    hideIcons = false,
    onClick: ((...args: unknown[]) => void) | undefined = undefined,
    hideTooltip = false,
    useLongShipTitle = false,
): React.ReactNode => {
    return (
        <ConditionalVehicleLink onClick={onClick}>
            <WoWSEntity
                type={ItemTypes.VEHICLES}
                id={id}
                presentation={{
                    withTooltip: !hideTooltip,
                    hideNationIcon: hideIcons,
                    hideTypeIcon: hideIcons,
                    useLongShipTitle: useLongShipTitle,
                }}
            />
        </ConditionalVehicleLink>
    );
};

export const shipDescription = (id: string): React.ReactNode => (
    <WoWSEntity
        type={ItemTypes.VEHICLES}
        id={id}
        presentation={{
            style: PresentationStyles.DESCRIPTION,
        }}
    />
);

export const entityType = (id: string): React.ReactNode => (
    <WoWSEntity
        type={ItemTypes.VEHICLES}
        id={id}
        presentation={{
            style: PresentationStyles.TYPE,
        }}
    />
);

export const compensation = (multiplier = 1, compensations: Array<CompensationItem> = []): React.ReactNode => (
    <div className={'compensation-inline-row'}>
        {compensations.map((compensationItem, index) => {
            return (
                <WoWSEntity
                    key={`compensation_${index}`}
                    className={'we-compensation'}
                    type={compensationItem.type}
                    amount={compensationItem.amount * multiplier}
                    presentation={{
                        withTooltip: true,
                    }}
                />
            );
        })}
    </div>
);

export const currency = (_currency: string, amount = 0): React.ReactNode => (
    <WoWSEntity
        key={`${_currency}_${amount}`}
        type={_currency}
        amount={amount}
        presentation={{
            withTooltip: true,
        }}
    />
);

export const gold = (amount = 0, isShowTooltip = true): React.ReactNode => (
    <WoWSEntity
        key={`gold_${amount}`}
        id={Currencies.GOLD}
        type={EntityTypes.CURRENCY}
        amount={amount}
        presentation={{
            withTooltip: isShowTooltip,
        }}
    />
);

export const specialReward = (reward: Api.IReward): React.ReactNode => {
    const local = preloaded.settings.languageCode;
    const { id, type, amount } = reward.gameRewards[0];
    if (type === 'ship' && typeof id === 'number') {
        return ship(id, true);
    }

    return (
        <WoWSEntity
            className={classnames({
                [styles.reward]: LANGUAGE_LOWER_FIRST_LETTER.includes(local),
            })}
            type={type as string}
            id={id}
            amount={amount as number}
            presentation={{
                withTooltip: true,
            }}
        />
    );
};
